import { Link } from "@inertiajs/react"
import { ArrowRight, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { ServiceIcon } from "@/components/tuw/post/EntryPicker"

// Shared draft card for the two multi-draft surfaces (S-PM-DRAFTS strip and
// S-POST-ENTRY split) plus a single source of card content that never drifts
// from the dashboard banner (tuw-app:P-007). One card renders from the shared
// CustomerDraftCardPresenter payload:
//   { id, name, icon, typeLabel, kind, stepNum, stepTotal, stepLabel, pct,
//     editedLabel, resumeRoute }
//
// The two variants differ only in the top-right label: the strip shows a static
// "Draft" pill (design draftsStrip .pill), the split shows the draft's kind
// ("Bundle" / "Service" / "N services", design dr-card .dr-kind).
//
// The lead glyph resolves through the shared ServiceIcon resolver from
// EntryPicker (tuw-app:P-007) — the same map that renders the picker's service
// tiles — so the card's icon never drifts from the catalog. The presenter's
// glyph names come from CustomerPostProjectPresenter::SERVICES / BUNDLES (:icon)
// plus Grid for a multi-service build-your-own draft; unknown names fall back to
// FileText inside ServiceIcon.

export default function DraftCard({ draft, variant = "strip", onDiscard }) {
  return (
    <div
      data-testid="draft-card"
      className="flex items-center gap-3.5 rounded-2xl border border-border bg-card px-4 py-3.5 max-[561px]:flex-wrap"
    >
      <span className="grid size-[42px] shrink-0 place-items-center rounded-[11px] border border-border bg-secondary">
        <ServiceIcon name={draft.icon} className="size-[21px] text-foreground" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-display text-[14px] font-bold tracking-tight text-foreground">
            {draft.name || "Untitled project"}
          </h3>
          {variant === "strip" ? (
            <span className="shrink-0 rounded-[5px] bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-primary">
              Draft
            </span>
          ) : (
            <span className="shrink-0 rounded-[5px] bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
              {draft.kind}
            </span>
          )}
        </div>

        {/* ≤560 the meta line un-nowraps and wraps onto a second line instead of
            ellipsing away the step label (design `.dr-meta{white-space:normal}`,
            P8-H5 entry residuals). */}
        <p className="mt-0.5 truncate text-[12px] text-muted-foreground max-[561px]:whitespace-normal">
          {draft.typeLabel} · <b className="font-semibold text-foreground">{draft.stepLabel}</b> ·{" "}
          step {draft.stepNum} of {draft.stepTotal}
        </p>

        <div className="mt-2 flex items-center gap-2.5">
          <Progress value={draft.pct} className="h-[5px] w-[130px]" />
          <span className="text-[11px] font-semibold text-ink-3">{draft.editedLabel}</span>
        </div>
      </div>

      {/* ≤560 the card wraps and the actions cluster takes its own full-width
          line (design `.dcard{flex-wrap}` + `.dacts{width:100%}`). Both DraftCard
          surfaces — the Projects strip and the post-entry split — inherit it. */}
      <div className="flex shrink-0 items-center gap-2 max-[561px]:w-full max-[561px]:justify-end">
        <Button asChild size="sm">
          <Link href={draft.resumeRoute}>
            Resume <ArrowRight className="size-4" strokeWidth={2.2} />
          </Link>
        </Button>
        {/* Raw icon button permitted per Hallmark R7; opens the DS confirm. */}
        <button
          type="button"
          onClick={() => onDiscard?.(draft)}
          aria-label="Discard draft"
          className="grid size-8 shrink-0 place-items-center rounded-lg border border-line-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
        >
          <Trash2 className="size-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  )
}
